import type { DateRange as RdpDateRange, Matcher } from "react-day-picker";

import dayjs, { type Dayjs } from "@eisox/dayjs";

interface DateRange {
  from: Dayjs | null;
  to: Dayjs | null;
}

const EMPTY_DATE_RANGE: DateRange = { from: null, to: null };

// Picking a day must keep typed time.
const withDayOf = (previous: Dayjs | null | undefined, date: Date): Dayjs => {
  const picked = dayjs(date);
  return previous ? previous.year(picked.year()).month(picked.month()).date(picked.date()) : picked;
};

const toRdpDate = (value: Dayjs | null | undefined): Date | undefined => value?.toDate();

const toRdpRange = (range: DateRange | undefined): RdpDateRange | undefined =>
  range?.from || range?.to ? { from: toRdpDate(range.from), to: toRdpDate(range.to) } : undefined;

const fromRdpRange = (range: RdpDateRange | undefined, previous: DateRange): DateRange => ({
  from: range?.from ? withDayOf(previous.from, range.from) : null,
  to: range?.to ? withDayOf(previous.to, range.to) : null,
});

const toDisabledMatchers = (min?: Dayjs, max?: Dayjs, disabledDates?: Matcher | Matcher[]): Matcher[] => {
  const matchers: Matcher[] = [];
  if (min) matchers.push({ before: min.toDate() });
  if (max) matchers.push({ after: max.toDate() });
  if (Array.isArray(disabledDates)) matchers.push(...disabledDates);
  else if (disabledDates) matchers.push(disabledDates);
  return matchers;
};

const toMonthBoundary = (value: Dayjs | undefined): Date | undefined => value?.startOf("month").toDate();

export { EMPTY_DATE_RANGE, fromRdpRange, toDisabledMatchers, toMonthBoundary, toRdpDate, toRdpRange, withDayOf };
export type { DateRange };
